import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Heart, History, Radio, Search, Keyboard } from "lucide-react";
import { useStore } from "@/lib/store";
import { useXC, useSlice } from "@/lib/hooks";
import { asList, liveSources, catchupSources } from "@/lib/xc";
import CategoryRail from "@/components/CategoryRail";
import Player from "@/components/Player";
import { NowNext, CatchupList } from "@/components/LiveInfo";

const toItem = (s) => ({
  type: "live",
  id: s.stream_id,
  name: s.name,
  icon: s.stream_icon,
  category_id: s.category_id,
  archive: Number(s.tv_archive) === 1,
  archiveDays: Number(s.tv_archive_duration) || 0,
});

function ChannelRow({ ch, num, on, onPlay }) {
  const { isFav } = useStore();
  return (
    <button
      onClick={() => onPlay(ch)}
      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${on ? "bg-[#1A1E2E]" : "hover:bg-[#11141F]"}`}
      data-testid={`channel-item-${ch.id}`}
    >
      <span className="w-7 text-[11px] font-mono text-slate-600 text-right shrink-0">{num}</span>
      <div className="w-10 h-10 rounded-lg bg-[#1A1E2E] overflow-hidden shrink-0 grid place-items-center">
        {ch.icon ? <img src={ch.icon} alt="" loading="lazy" className="w-full h-full object-contain" onError={(e) => (e.currentTarget.style.display = "none")} /> : <Radio className="w-4 h-4 text-slate-600" />}
      </div>
      <span className={`text-sm truncate flex-1 ${on ? "text-white" : "text-slate-300"}`}>{ch.name}</span>
      {ch.archive && <History className="w-3.5 h-3.5 text-[#00E5FF]" title="Catch-up available" />}
      {on && <span className="w-2 h-2 rounded-full bg-[#E50914] xc-pulse" />}
      {isFav("live", ch.id) && <Heart className="w-3.5 h-3.5 fill-[#E50914] text-[#E50914]" />}
    </button>
  );
}

function Osd({ osd }) {
  if (!osd) return null;
  return (
    <div className="absolute top-5 left-5 z-10 rounded-xl bg-black/75 backdrop-blur-md border border-white/10 px-5 py-3 flex items-center gap-4 pointer-events-none xc-rise" data-testid="channel-osd">
      <span className="font-mono text-3xl font-bold text-[#00E5FF]" data-testid="channel-osd-number">
        {osd.num}
        {osd.typing && <span className="xc-pulse">_</span>}
      </span>
      <span className={`font-heading text-lg ${osd.typing ? "text-slate-400" : ""}`} data-testid="channel-osd-name">{osd.name}</span>
    </div>
  );
}

export default function LiveTV() {
  const { active, isFav, toggleFav, pushRecent, liveFmt, setLiveFmt } = useStore();
  const loc = useLocation();
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const [ch, setCh] = useState(loc.state?.channel || null);
  const [catchup, setCatchup] = useState(loc.state?.catchup || null);
  const [fmt, setFmt] = useState(null);
  const [osd, setOsd] = useState(null);
  const osdTimer = useRef(null);
  const digits = useRef("");
  const digitTimer = useRef(null);
  const cats = useXC(active, "get_live_categories");
  const streams = useXC(active, "get_live_streams", cat ? { category_id: cat } : {});
  const list = useMemo(() => {
    const all = asList(streams.data).map(toItem);
    return q ? all.filter((c) => c.name?.toLowerCase().includes(q.toLowerCase())) : all;
  }, [streams.data, q]);
  const [visible, onScroll, ensure] = useSlice(list, 150);

  useEffect(() => {
    if (!loc.state?.channel) return;
    setCh(loc.state.channel);
    setCatchup(loc.state.catchup || null);
    pushRecent(loc.state.channel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.state]);

  const play = (c) => {
    setCh(c);
    setCatchup(null);
    setFmt(null);
    pushRecent(c);
  };

  useEffect(() => {
    const STEP = { ArrowUp: -1, ArrowDown: 1, PageUp: -1, PageDown: 1 };
    const showOsd = (o, ms = 2500) => {
      setOsd(o);
      clearTimeout(osdTimer.current);
      if (ms) osdTimer.current = setTimeout(() => setOsd(null), ms);
    };
    const jump = (ni) => {
      const next = list[ni];
      play(next);
      ensure(ni);
      showOsd({ num: ni + 1, name: next.name });
      setTimeout(() => document.querySelector(`[data-testid="channel-item-${next.id}"]`)?.scrollIntoView({ block: "nearest" }), 50);
    };
    const commit = () => {
      clearTimeout(digitTimer.current);
      const n = parseInt(digits.current, 10);
      digits.current = "";
      if (n >= 1 && n <= list.length) jump(n - 1);
      else showOsd({ num: n, name: "No channel with this number" });
    };
    const h = (e) => {
      if (!list.length || e.target.closest?.("input,textarea,select,[role=dialog]")) return;
      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault();
        digits.current = (digits.current + e.key).slice(-4);
        showOsd({ num: digits.current, name: "Type a channel number…", typing: true }, 0);
        clearTimeout(digitTimer.current);
        digitTimer.current = setTimeout(commit, 1500);
        return;
      }
      if (e.key === "Enter" && digits.current) {
        e.preventDefault();
        commit();
        return;
      }
      const d = STEP[e.key];
      if (!d) return;
      e.preventDefault();
      e.stopPropagation();
      const i = list.findIndex((c) => String(c.id) === String(ch?.id));
      jump(i < 0 ? 0 : (i + d + list.length) % list.length);
    };
    window.addEventListener("keydown", h, true);
    return () => window.removeEventListener("keydown", h, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, ch]);

  const sources = ch ? (catchup ? catchupSources(active, ch.id, catchup) : liveSources(active, ch.id, liveFmt === "ts")) : [];
  const onSourceOk = (t) => {
    setFmt(t);
    if (!catchup && t !== liveFmt) setLiveFmt(t);
  };

  return (
    <div className="flex h-full" data-testid="live-tv-page">
      <CategoryRail categories={asList(cats.data)} active={cat} onSelect={setCat} loading={cats.loading} testPrefix="live" />
      <section className="w-[320px] shrink-0 border-r border-[#212738] flex flex-col">
        <div className="p-3 border-b border-[#212738]">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search channels" className="xc-input !pl-9" data-testid="channel-search-input" />
          </div>
          <div className="flex items-center justify-between mt-2.5 px-1">
            <span className="xc-label">{list.length} channels</span>
            <span className="xc-label flex items-center gap-1.5" title="Arrow keys or Page Up / Page Down change channel. Type a number to jump to it.">
              <Keyboard className="w-3 h-3" /> ↑↓ · 0-9
            </span>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto xc-scroll" onScroll={onScroll} data-testid="channel-list">
          {streams.loading && <div className="p-4 text-sm text-slate-500">Loading channels…</div>}
          {streams.error && <div className="p-4 text-sm text-[#FF5A62]">{streams.error}</div>}
          {visible.map((c, i) => (
            <ChannelRow key={c.id} ch={c} num={i + 1} on={String(ch?.id) === String(c.id)} onPlay={play} />
          ))}
        </div>
      </section>
      <section className="flex-1 min-w-0 flex flex-col overflow-y-auto xc-scroll">
        <div className="aspect-video bg-black w-full max-h-[70vh] relative shrink-0">
          <Osd osd={osd} />
          {ch ? (
            <Player sources={sources} live={!catchup} onSourceOk={onSourceOk} />
          ) : (
            <div className="w-full h-full grid place-items-center text-center px-6" data-testid="live-empty">
              <div>
                <Radio className="w-10 h-10 mx-auto text-[#38425C]" />
                <p className="font-heading text-xl mt-4">Pick a channel to start watching</p>
                <p className="text-sm text-slate-500 mt-1">Choose a category, then a channel. Use ↑ ↓ to surf.</p>
              </div>
            </div>
          )}
        </div>
        {ch && (
          <div className="p-6 space-y-5">
            <div className="flex items-center gap-4 flex-wrap">
              {catchup ? (
                <span className="rounded-md bg-[#00E5FF] text-black px-2 py-0.5 text-[11px] font-mono font-bold tracking-widest" data-testid="catchup-badge">CATCH-UP</span>
              ) : (
                <span className="flex items-center gap-1.5 rounded-md bg-[#E50914] px-2 py-0.5 text-[11px] font-mono font-bold tracking-widest">
                  <span className="w-1.5 h-1.5 rounded-full bg-white xc-pulse" /> LIVE
                </span>
              )}
              {fmt && <span className="rounded-md border border-[#38425C] px-2 py-0.5 text-[11px] font-mono text-slate-400" data-testid="stream-format-badge">{fmt.toUpperCase()}</span>}
              <h2 className="font-heading text-2xl font-bold tracking-tight truncate flex-1" data-testid="live-channel-title">{ch.name}</h2>
              {catchup && (
                <button onClick={() => setCatchup(null)} className="xc-btn-primary" data-testid="back-to-live-button">
                  <Radio className="w-4 h-4" /> Back to live
                </button>
              )}
              <button onClick={() => toggleFav(ch)} className="xc-btn-ghost" data-testid="live-fav-button">
                <Heart className={`w-4 h-4 ${isFav("live", ch.id) ? "fill-[#E50914] text-[#E50914]" : ""}`} />
                {isFav("live", ch.id) ? "Favourite" : "Add to favourites"}
              </button>
            </div>
            {catchup ? <div className="text-sm text-slate-400" data-testid="catchup-now-title">Watching from the archive: {catchup.titleText}</div> : <NowNext channel={ch} />}
            {ch.archive && <CatchupList channel={ch} current={catchup} onPick={setCatchup} />}
          </div>
        )}
      </section>
    </div>
  );
}
